import AsyncStorage from '@react-native-async-storage/async-storage';
import { RouteProp } from '@react-navigation/native';
import { Camera, CameraType, FaceDetectionResult } from 'expo-camera';
import * as Device from 'expo-device';
import * as FaceDetector from 'expo-face-detector';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import * as Location from 'expo-location';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Image, Platform, SafeAreaView, StyleSheet, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import { NavigationParams, NavigationRoute, NavigationScreenProp } from 'react-navigation';
import { useMutation } from 'react-query';
import { QueryContext } from '../components/QueryProvider';
import { postCheckin, postCheckout } from '../services/ApiService';
import { dataURItoBlob } from '../services/uriToBlob';


export interface ICheckinScreenProps {
    navigation: NavigationScreenProp<NavigationRoute>;
    route: RouteProp<NavigationParams>;
}

const faceSetting = {
    detectLandmarks: FaceDetector.FaceDetectorLandmarks.all,
    minDetectionInterval: 1000,
    mode: FaceDetector.FaceDetectorMode.accurate,
    runClassifications: FaceDetector.FaceDetectorClassifications.all,
    tracking: true
}

type formData = {
    device: string;
    latitude: string;
    longitude: string;
    photo: any;
}

export function CameraScreen({ route, navigation }: ICheckinScreenProps) {
    const query = React.useContext(QueryContext);
    const [location, setLocation] = useState<{ longitude: string, latitude: string } | null>(null);
    const [permission, requestPermission] = Camera.useCameraPermissions();
    const camera = useRef<Camera>(null);
    const [picture, setPicture] = useState<string | null | undefined>(null);
    const { mutate: mutateCheckin, isLoading: isLoadingCheckin } = useMutation(async (data: formData) => {
        const token = await AsyncStorage.getItem('token');
        return postCheckin(
            token?.toString() ?? '',
            data.device,
            data.latitude,
            data.longitude,
            data.photo
        );
    }, {
        onSettled: () => {
            query.invalidateQueries('attendance_status');
            navigation.goBack();
        }
    });

    const { mutate: mutateCheckout, isLoading: isLoadingCheckout } = useMutation(async (data: formData) => {
        const token = await AsyncStorage.getItem('token');
        return postCheckout(
            token?.toString() ?? '',
            data.device,
            data.latitude,
            data.longitude,
            data.photo
        );
    }, {
        onSettled: () => {
            query.invalidateQueries('attendance_status');
            navigation.goBack();
        }
    });

    const requestLocationPermissionAndGetCurrentPosition = async () => {
        let { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
            alert('Permission to access location was denied');
            navigation.navigate('Home');
            return;
        }

        let localLocation = await Location.getCurrentPositionAsync({
            accuracy: Location.Accuracy.Balanced,
        });

        let { latitude, longitude } = localLocation.coords;

        setLocation({ latitude: latitude.toString(), longitude: longitude.toString() });
    };

    const requestPermissionAndLogStatus = async () => {
        const { status } = await requestPermission();
        if (status !== 'granted') {
            alert('Permission to access camera was denied');
            navigation.navigate('Home')
            return;
        }
    };

    useEffect(() => {
        requestLocationPermissionAndGetCurrentPosition();
        requestPermissionAndLogStatus();
    }, []);

    async function handleFaceDetected({ faces }: FaceDetectionResult) {
        if (faces.length > 0) {
            if (typeof faces[0].smilingProbability !== 'undefined' && faces[0].smilingProbability > 0.5) {
                console.log('Smiling');
                const photo = await camera.current?.takePictureAsync();
                await compressImage(photo?.uri ?? '');
            } else {
                console.log('Not smiling');
            }
        }
    }

    async function compressImage(uri: string) {
        const compressedImage = await manipulateAsync(
            uri,
            [{ resize: { width: 500 } }],
            { compress: 0.7, format: SaveFormat.JPEG }
        );
        setPicture(compressedImage.uri);
        console.log({ image: compressedImage.uri });
        requestLocationPermissionAndGetCurrentPosition();
    }

    async function takePicture() {
        const photo = await camera.current?.takePictureAsync();
        await compressImage(photo?.uri ?? '');
    }

    function handleSubmit() {
        let formData: formData = {
            device: Device.deviceName ?? 'Unknown Device',
            latitude: location?.latitude?.toString() ?? '',
            longitude: location?.longitude?.toString() ?? '',
            photo: dataURItoBlob(picture) ?? '',
        };

        if (route.params?.checkout) {
            mutateCheckout(formData);
        } else {
            mutateCheckin(formData);
        }
    }

    if (!permission) {
        return <View />;
    }

    return (
        <SafeAreaView style={styles.container}>
            {picture ? (
                <>
                    <Image
                        source={{ uri: picture }}
                        style={styles.picture}
                    ></Image>
                    <Button
                        mode='contained'
                        icon='refresh'
                        buttonColor='#f33'
                        onPress={() => setPicture(null)}
                        style={styles.buttonRetake}>
                        Ulang
                    </Button>
                </>
            ) : (
                <>
                    {Platform.OS != 'web' && (
                        <View style={styles.alertContainer}>
                            <Text style={styles.alertText}>Senyum untuk Foto</Text>
                        </View>
                    )}

                    <Camera
                        ratio='1:1'
                        faceDetectorSettings={faceSetting}
                        onFacesDetected={handleFaceDetected}
                        style={styles.cameraContainer}
                        type={CameraType.front}
                        ref={camera}>
                        <View style={styles.cameraOverlay} />
                    </Camera>
                    <Button
                        mode='contained'
                        icon='camera'
                        buttonColor='#15803d'
                        onPress={() => takePicture()}
                        style={styles.buttonRetake}>
                        Kudaaa
                    </Button>

                </>
            )}

            <Button
                onPress={handleSubmit}
                disabled={!picture || isLoadingCheckin || isLoadingCheckout}
                mode='contained'
                style={styles.button}
                labelStyle={styles.buttonLabel}>
                {(isLoadingCheckin || isLoadingCheckout) ? 'Semoga koneksimu lancar...' : (route.params?.checkout ? 'Checkout' : 'Checkin')}
            </Button>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    buttonRetake: {
        marginTop: 20,
        alignContent: 'center',
    },
    picture: {
        width: 300,
        height: 300,
    },
    buttonLabel: {
        fontSize: 20,
    },
    button: {
        alignSelf: 'stretch',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        borderRadius: 0,
        padding: 10,
    },
    alertContainer: {
        backgroundColor: 'yellow',
        padding: 10,
        borderRadius: 15,
        marginBottom: 20,

    },
    alertText: {
        color: 'black',
        fontSize: 18,
        textAlign: 'center',
    },
    textSmile: {
        textAlign: 'center',
        maxWidth: 300,
        backgroundColor: '#ccc',
        margin: 20,
        borderRadius: 10,
        padding: 10,
    },
    container: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cameraContainer: {
        width: 300,
        height: 300,
        overflow: 'hidden',
        maxHeight: 300,
    },
    cameraOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000',
        opacity: 0.2,
        borderRadius: 300,
    },
});